import React, { useState } from 'react';
import { 
  LayoutDashboard, Calendar, AlertCircle, LogOut, 
  UserSquare, GraduationCap, UserPlus, ShieldCheck, 
  Database, Settings, X
} from 'lucide-react';

// ✅ CONEXIÓN AL SISTEMA NERVIOSO CENTRAL (Rutas Absolutas)
import { useGlobal } from 'context/GlobalContext';

// ✅ IMPORTACIÓN DE VISTAS DEL MÓDULO (Rutas Absolutas)
import GestionSalones from 'pages/admin/GestionSalones';
import PerfilAdmin from 'pages/admin/PerfilAdmin';
import ProgramadorEventos from 'pages/admin/ProgramadorEventos';
import CentroIncidencias from 'pages/admin/CentroIncidencias';
import MuroCelebraciones from 'pages/admin/MuroCelebraciones';
import GestionMaestros from 'pages/admin/GestionMaestros';
import GestionAyudantes from 'pages/admin/GestionAyudantes';
import GestionLegal from 'pages/admin/GestionLegal';
import BaseDatosKids from 'pages/admin/BaseDatosKids';
import AjustesSistema from 'pages/admin/AjustesSistema';

const AdminModule = ({ alSalir }) => {
  // 1. Extraemos los datos globales para supervisión general
  const { ninos, alertasAdmin } = useGlobal();
  
  const [tabActiva, setTabActiva] = useState('logistica');

  const menuItems = [
    { id: 'logistica', titulo: 'Salones', icono: LayoutDashboard },
    { id: 'kids', titulo: 'Auditoría', icono: Database },
    { id: 'maestros', titulo: 'Maestros', icono: GraduationCap },
    { id: 'ayudantes', titulo: 'Staff', icono: UserPlus },
    { id: 'eventos', titulo: 'Eventos', icono: Calendar },
    { id: 'incidencias', titulo: 'Alertas', icono: AlertCircle },
    { id: 'ajustes', titulo: 'Sistema', icono: Settings },
    { id: 'legal', titulo: 'Legal', icono: ShieldCheck },
    { id: 'perfil', titulo: 'Admin', icono: UserSquare }
  ];

  // 2. RENDERIZADO DINÁMICO
  const renderPantallaActiva = () => {
    // Inyección de estados reactivos a los componentes hijos
    const propsComunes = { ninos, alertasAdmin };

    switch (tabActiva) {
      case 'logistica': return <GestionSalones {...propsComunes} />;
      case 'kids': return <BaseDatosKids {...propsComunes} />;
      case 'maestros': return <GestionMaestros />;
      case 'ayudantes': return <GestionAyudantes />;
      case 'eventos': return <ProgramadorEventos />;
      case 'incidencias': return <CentroIncidencias {...propsComunes} />;
      case 'celebraciones': return <MuroCelebraciones />;
      case 'legal': return <GestionLegal />;
      case 'ajustes': return <AjustesSistema />;
      case 'perfil': return <PerfilAdmin />;
      default: return <GestionSalones {...propsComunes} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row font-sans text-left">
      
      {/* SIDEBAR - TORRE DE CONTROL */}
      <aside className="hidden md:flex w-64 lg:w-72 bg-slate-900 flex-col sticky top-0 h-screen shadow-2xl z-50">
        <div className="p-8">
          <h1 className="text-2xl font-black text-white italic tracking-tighter uppercase leading-none">IgleKids</h1>
          <p className="text-[10px] font-black text-amber-500 uppercase tracking-[0.3em] mt-2 italic">Torre de Control</p>
        </div>

        <nav className="flex-1 px-4 space-y-2 overflow-y-auto hide-scrollbar">
          {menuItems.map((item) => {
            const activo = tabActiva === item.id;
            const Icono = item.icono;
            const tieneAlertas = item.id === 'incidencias' && alertasAdmin.length > 0;

            return (
              <button
                key={item.id}
                onClick={() => setTabActiva(item.id)}
                className={`w-full flex items-center justify-between px-4 py-4 rounded-2xl transition-all duration-300 group ${
                  activo ? 'bg-amber-500 text-slate-900 shadow-lg' : 'text-slate-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-4">
                  <Icono size={20} strokeWidth={activo ? 3 : 2} />
                  <span className={`text-[10px] font-black uppercase tracking-widest ${activo ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'}`}>
                    {item.titulo}
                  </span>
                </div>
                {tieneAlertas && !activo && (
                  <span className="w-2 h-2 bg-rose-500 rounded-full animate-pulse shadow-[0_0_10px_#f43f5e]" />
                )}
              </button>
            );
          })}
        </nav>

        <div className="p-6 border-t border-white/10">
          <button 
            onClick={alSalir}
            className="w-full flex items-center gap-4 px-4 py-4 rounded-2xl text-rose-400 hover:bg-rose-500/10 transition-all font-black text-[10px] uppercase tracking-widest active:scale-95"
          >
            <LogOut size={20} /> Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* HEADER MÓVIL (VISIBLE EN TABLETS) */}
      <header className="md:hidden bg-slate-900 px-6 py-5 flex items-center justify-between shadow-lg sticky top-0 z-50 border-b border-white/10">
        <div className="flex flex-col text-white text-left">
          <h1 className="text-xl font-black italic uppercase leading-none">IgleKids</h1>
          <span className="text-[9px] font-black text-amber-500 uppercase tracking-widest mt-1">Admin — Casa de Fe</span>
        </div>
        <button onClick={alSalir} className="text-rose-400 p-2 bg-white/5 rounded-xl active:scale-90"><LogOut size={20} /></button>
      </header>

      {/* CONTENEDOR DE TRABAJO */}
      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 p-4 md:p-10 lg:p-12 max-w-[1600px] mx-auto w-full">
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
            {renderPantallaActiva()}
          </div>
        </main>
      </div>

      {/* NAV INFERIOR (SCROLLABLE PARA MÓVIL) */}
      <nav className="md:hidden fixed bottom-0 w-full bg-white border-t-2 border-slate-100 pb-safe z-50 shadow-[0_-10px_40px_rgba(0,0,0,0.1)] overflow-x-auto hide-scrollbar">
        <div className="flex justify-between items-center px-6 py-4 min-w-max gap-8">
          {menuItems.map((item) => {
            const activo = tabActiva === item.id;
            const Icono = item.icono;
            return (
              <button 
                key={item.id} 
                onClick={() => setTabActiva(item.id)} 
                className={`flex flex-col items-center gap-1 transition-all ${activo ? 'text-amber-600 scale-110' : 'text-slate-400'}`}
              >
                <div className="relative">
                  <Icono size={22} strokeWidth={activo ? 3 : 2} />
                  {item.id === 'incidencias' && alertasAdmin.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-rose-500 rounded-full border-2 border-white animate-pulse" />
                  )}
                </div>
                <span className="text-[8px] font-black uppercase tracking-tighter">{item.titulo}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default AdminModule;